import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDocFromServer, getDocsFromServer, collection, updateDoc } from 'firebase/firestore';
import type { DocumentData, DocumentSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Event, RSVP, Unit, UnitType } from '@/types';
import { unitDisplaysNumber } from '@/lib/unit-number-display';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { useAuth } from '@/contexts/AuthContext';
import { showError } from '@/lib/swal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { generateBookingFormPdf } from '@/lib/pdf-generator';
import { normalizeTicketCoursesToFormatA } from '@/lib/ticket-courses';
import { sortBookingFormGuestsBySeatAsc } from '@/lib/booking-form-pdf-sort';
import {
  buildKitchenCombinedCourseSummaries,
  buildKitchenCourseSummaries,
  buildKitchenDietaryRequirements,
  buildKitchenFallbackMealCounts,
  buildKitchenTicketCourseSummaries,
  buildKitchenTicketQuantitySummary,
  getKitchenActiveRsvps,
  getKitchenNumbersDining,
  type TableAssignmentMap,
} from '@/lib/kitchen-report';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { isMultiTenantEvent } from '@/lib/ticket-mode';
import { loadEventSeatAssignments } from '@/lib/table-plan-seating';
import { expandMultiTenantGuestPlaceholders } from '@/lib/table-plan-guest-placeholders';

type MealDisplayMode = 'combine' | 'split';

interface KitchenFormData {
  contactTelephone: string;
  barTabContact: string;
  additionalItems: string;
  preferredDiningRoom: string;
  preferredDiningTime: string;
  roomLayout: string;
  candlesCandelabras: string;
  linenTablecloths: string;
  linenNapkins: string;
  gunRoom: string;
  barTabRequired: string;
  installation: string;
  election: string;
  aLaCarteMenu: string;
  mealDisplayMode: MealDisplayMode;
}

const LabelValueRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-12 border-b border-slate-300 last:border-b-0">
    <div className="col-span-4 flex items-center border-r border-slate-300 bg-[#1f3a75] px-4 py-3 text-sm font-semibold text-white">
      {label}
    </div>
    <div className="col-span-8 px-4 py-3 text-sm text-slate-800">{children}</div>
  </div>
);

/**
 * Shadcn Input / SelectTrigger use `bg-background` (dark in dark mode). This form is a fixed
 * light “paper” layout; scope controls so they stay readable on that surface.
 */
const KITCHEN_FORM_PAPER_CONTROL_CLASSES =
  '[&_input]:!border-slate-300 [&_input]:!bg-white [&_input]:!text-slate-900 [&_input]:!placeholder:text-slate-500 [&_input]:!ring-offset-white [&_textarea]:!border-slate-300 [&_textarea]:!bg-white [&_textarea]:!text-slate-900 [&_textarea]:!placeholder:text-slate-500 [&_textarea]:!ring-offset-white [&_button[role=combobox]]:!border-slate-300 [&_button[role=combobox]]:!bg-white [&_button[role=combobox]]:!text-slate-900 [&_button[role=combobox]]:!ring-offset-white [&_button[role=combobox]_svg]:!text-slate-600';

const SUBMISSION_SCHEDULE = [
  { day: 'Monday', returnBy: 'Wednesday' },
  { day: 'Tuesday', returnBy: 'Thursday' },
  { day: 'Wednesday', returnBy: 'Friday' },
  { day: 'Thursday', returnBy: 'Monday' },
  { day: 'Friday', returnBy: 'Tuesday' },
  { day: 'Saturday', returnBy: 'Tuesday' },
];

const ROOM_LAYOUT_OPTIONS = [
  { label: 'Sprigs', value: 'Sprigs' },
  { label: 'Round Tables', value: 'Round Tables' },
];

const YES_NO_OPTIONS = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const MEAL_DISPLAY_MODE_OPTIONS: Array<{ label: string; value: MealDisplayMode }> = [
  { label: 'Combine by meal choice', value: 'combine' },
  { label: 'Split by ticket type', value: 'split' },
];

/**
 * Seating comes from the Visual Table Planner's floor plans via the shared loader, so the room name
 * and seat numbers match the planner (not the stale single-plan mirror it used to write).
 */
async function fetchTableAssignmentsForEvent(
  unitId: string,
  eventId: string
): Promise<TableAssignmentMap> {
  return loadEventSeatAssignments(unitId, eventId, { fromServer: true });
}

/**
 * Attending diners. Multi-tenant bookings add a "Guest of …" row per ticket after the first (seated
 * by the planner), so numbers dining counts tickets and the guest list shows each seat. Placeholders
 * carry no responses, so meal, ticket and dietary totals are unchanged.
 */
function getKitchenDiners(event: Event | null, rsvps: RSVP[]): RSVP[] {
  const active = getKitchenActiveRsvps(rsvps);
  return isMultiTenantEvent(event) ? expandMultiTenantGuestPlaceholders(active) : active;
}

function mapRsvpDocument(id: string, data: DocumentData): RSVP {
  const responses = data.responses || {};
  return {
    id,
    ...data,
    responses: {
      ...responses,
      Dietary: Array.isArray(responses.Dietary) ? responses.Dietary :
              (responses.Dietary ? [responses.Dietary] : []),
      Allergies: typeof responses.Allergies === 'string' ? responses.Allergies : '',
    },
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    checkInTime: data.checkInTime?.toDate(),
  } as RSVP;
}

function parseUnitFromSnapshot(unitDoc: DocumentSnapshot): Unit | null {
  if (!unitDoc.exists()) {
    return null;
  }
  const unitData = unitDoc.data();
  if (!unitData) {
    return null;
  }
  return {
    id: unitDoc.id,
    name: unitData.name,
    unitNumber: unitData.unitNumber || unitData.number || '',
    typeId: unitData.typeId || '',
    members: unitData.members || [],
    createdAt: unitData.createdAt?.toDate() || new Date(),
    subscription: unitData.subscription,
    settings: unitData.settings || {},
    contactEmail: unitData.contactEmail,
    contactPhone: unitData.contactPhone,
    address: unitData.address,
    description: unitData.description,
  } as Unit;
}

function parseEventFromSnapshot(eventDoc: DocumentSnapshot, unitId: string): Event {
  const eventData = eventDoc.data();
  if (!eventData) {
    throw new Error('Event document has no data');
  }
  const eventDate = eventData.date?.toDate() || new Date();
  return {
    ...eventData,
    id: eventDoc.id,
    unitId,
    name: eventData.name,
    ceremonyType: eventData.ceremonyType,
    date: eventDate,
    location: eventData.location,
    venueId: eventData.venueId,
    questionnaire: eventData.questionnaire || [],
    courses: eventData.courses || [],
    tickets: eventData.tickets || [],
    maxCapacity: eventData.maxCapacity,
    currentAttendees: eventData.currentAttendees || 0,
    waitlistEnabled: eventData.waitlistEnabled || false,
    waitlist: eventData.waitlist || [],
    rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
    paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
    eventStatus: eventData.eventStatus,
    dresscode: eventData.dresscode,
    customInstructions: eventData.customInstructions,
    isPublic: eventData.isPublic ?? false,
    requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
    createdBy: eventData.createdBy,
    createdAt: eventData.createdAt?.toDate() || new Date(),
  } as Event;
}

async function fetchKitchenReportSnapshotFromServer(
  unitId: string,
  eventId: string
): Promise<{
  unit: Unit | null;
  event: Event;
  rsvps: RSVP[];
  tableAssignments: TableAssignmentMap;
} | null> {
  const unitDoc = await getDocFromServer(doc(db, 'units', unitId));
  const unit = parseUnitFromSnapshot(unitDoc);

  const eventDoc = await getDocFromServer(doc(db, 'units', unitId, 'events', eventId));
  if (!eventDoc.exists()) {
    return null;
  }
  const event = parseEventFromSnapshot(eventDoc, unitId);

  const rsvpsSnapshot = await getDocsFromServer(
    collection(db, 'units', unitId, 'events', eventId, 'rsvps')
  );
  const rsvps = rsvpsSnapshot.docs.map((d) => mapRsvpDocument(d.id, d.data()));

  const tableAssignments = await fetchTableAssignmentsForEvent(unitId, eventId);

  return { unit, event, rsvps, tableAssignments };
}

export default function KitchenReportPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [tableAssignments, setTableAssignments] = useState<Record<string, { roomName: string | null; tableName: string | null; seatNumber: number | null }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [formData, setFormData] = useState<KitchenFormData>({
    contactTelephone: '',
    barTabContact: '',
    additionalItems: '',
    preferredDiningRoom: '',
    preferredDiningTime: '',
    roomLayout: '',
    candlesCandelabras: '',
    linenTablecloths: '',
    linenNapkins: '',
    gunRoom: '',
    barTabRequired: '',
    installation: '',
    election: '',
    aLaCarteMenu: '',
    mealDisplayMode: 'split',
  });
  const [formInitialised, setFormInitialised] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    setFormInitialised(false);
  }, [unitId, eventId]);

  // Load platform unit types to decide whether the unit shows a unit number
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDocFromServer(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          setUnitTypes((settingsDoc.data().unitTypes || []) as UnitType[]);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId) {
        navigate('/dashboard');
        return;
      }

      try {
        setLoading(true);

        const snapshot = await fetchKitchenReportSnapshotFromServer(unitId, eventId);
        if (!snapshot) {
          navigate('/dashboard');
          return;
        }

        setUnit(snapshot.unit);
        setEvent(snapshot.event);
        setRsvps(snapshot.rsvps);
        setTableAssignments(snapshot.tableAssignments);
      } catch (error) {
        console.error('Error fetching data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load booking form data';
        setError(errorMessage);
        showError(`Unable to load data: ${errorMessage}`);
        // Allow user to retry instead of immediately redirecting
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, location.key, navigate]);

  const eventExtras = useMemo(() => {
    if (!event) {
      return {};
    }

    return event as unknown as Record<string, any>;
  }, [event]);

  const persistEventUpdate = useCallback(async (
    stateKey: keyof KitchenFormData,
    docField: string,
    value: string | boolean | null
  ) => {
    if (!unitId || !eventId) {
      return;
    }

    try {
      setSavingField(stateKey);
      const eventRef = doc(db, 'units', unitId, 'events', eventId);
      await updateDoc(eventRef, {
        [docField]: value,
      });
    } catch (persistError) {
      console.error('Error updating event field:', persistError);
      const message =
        persistError instanceof Error ? persistError.message : 'Unknown error saving changes';
      showError(`Unable to save changes: ${message}`);
    } finally {
      setSavingField(null);
    }
  }, [unitId, eventId]);

  const handleTextFieldChange = useCallback(
    (stateKey: keyof KitchenFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setFormData((prev) => ({
        ...prev,
        [stateKey]: value,
      }));
    }, []);

  const handleTextFieldBlur = useCallback(
    (stateKey: keyof KitchenFormData, docField: string) =>
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      const normalisedValue = value.trim().length === 0 ? null : value.trim();
      void persistEventUpdate(stateKey, docField, normalisedValue);
    }, [unitId, eventId, persistEventUpdate]);

  const handleSelectChange = useCallback(
    (stateKey: keyof KitchenFormData, docField: string, asBoolean = false) =>
    (value: string) => {
      setFormData((prev) => ({
        ...prev,
        [stateKey]: value,
      }));

      let normalisedValue: string | boolean | null = value;
      if (value === '') {
        normalisedValue = null;
      } else if (asBoolean) {
        normalisedValue = value === 'Yes';
      }

      void persistEventUpdate(stateKey, docField, normalisedValue);
    }, [unitId, eventId, persistEventUpdate]);

  const inferredInstallation = useMemo(() => {
    if (!event?.ceremonyType) {
      return null;
    }
    return event.ceremonyType.toLowerCase().includes('installation');
  }, [event?.ceremonyType]);

  const inferredElection = useMemo(() => {
    if (!event?.ceremonyType) {
      return null;
    }
    return event.ceremonyType.toLowerCase().includes('election');
  }, [event?.ceremonyType]);

  useEffect(() => {
    if (!event || formInitialised) {
      return;
    }

    setFormData({
      contactTelephone: (eventExtras?.contactPhone as string) || unit?.contactPhone || '',
      barTabContact: (eventExtras?.barTabContact as string) || '',
      additionalItems: (eventExtras?.additionalRequirements as string) || '',
      preferredDiningRoom: (eventExtras?.diningRoom as string) || '',
      preferredDiningTime: (eventExtras?.diningTime as string) || '',
      roomLayout: (eventExtras?.roomLayout as string) || '',
      candlesCandelabras:
        typeof eventExtras?.candlesRequired === 'boolean'
          ? eventExtras.candlesRequired
            ? 'Yes'
            : 'No'
          : '',
      linenTablecloths:
        typeof eventExtras?.linenTablecloths === 'boolean'
          ? eventExtras.linenTablecloths
            ? 'Yes'
            : 'No'
          : '',
      linenNapkins:
        typeof eventExtras?.linenNapkins === 'boolean'
          ? eventExtras.linenNapkins
            ? 'Yes'
            : 'No'
          : '',
      gunRoom:
        typeof eventExtras?.gunRoom === 'boolean'
          ? eventExtras.gunRoom
            ? 'Yes'
            : 'No'
          : '',
      barTabRequired:
        typeof eventExtras?.barTabRequired === 'boolean'
          ? eventExtras.barTabRequired
            ? 'Yes'
            : 'No'
          : '',
      installation:
        typeof eventExtras?.installation === 'boolean'
          ? eventExtras.installation
            ? 'Yes'
            : 'No'
          : inferredInstallation === null
            ? ''
            : inferredInstallation
              ? 'Yes'
              : 'No',
      election:
        typeof eventExtras?.election === 'boolean'
          ? eventExtras.election
            ? 'Yes'
            : 'No'
          : inferredElection === null
            ? ''
            : inferredElection
              ? 'Yes'
              : 'No',
      aLaCarteMenu:
        (eventExtras?.aLaCarteMenu as string) ||
        (eventExtras?.aLaCarteMenuSelection as string) ||
        '',
      mealDisplayMode:
        eventExtras?.mealDisplayMode === 'combine' || eventExtras?.mealDisplayMode === 'split'
          ? (eventExtras.mealDisplayMode as MealDisplayMode)
          : 'split',
    });
    setFormInitialised(true);
  }, [event, eventExtras, unit, formInitialised, inferredInstallation, inferredElection]);

  // Filter out RSVPs with apologies or declined/cancelled status
  const activeRsvps = useMemo(() => getKitchenDiners(event, rsvps), [event, rsvps]);

  const numbersDining = useMemo(
    () => getKitchenNumbersDining(activeRsvps, rsvps),
    [activeRsvps, rsvps],
  );

  const dietaryRequirements = useMemo(
    () => buildKitchenDietaryRequirements(activeRsvps, tableAssignments),
    [activeRsvps, tableAssignments]
  );

  const courseSummaries = useMemo(
    () => buildKitchenCourseSummaries(event, activeRsvps),
    [event, activeRsvps]
  );

  const fallbackMealCounts = useMemo(
    () => buildKitchenFallbackMealCounts(activeRsvps),
    [activeRsvps]
  );

  const ticketCourseSummaries = useMemo(
    () => buildKitchenTicketCourseSummaries(event, activeRsvps),
    [event, activeRsvps]
  );

  // Combined view: aggregate selections from all tickets into a single per-course summary
  const combinedCourseSummaries = useMemo(
    () => buildKitchenCombinedCourseSummaries(ticketCourseSummaries, courseSummaries),
    [ticketCourseSummaries, courseSummaries]
  );

  // Multi-tenant events have no meal choices; show ticket quantities purchased instead.
  const isMultiTenant = isMultiTenantEvent(event);
  const ticketQuantitySummary = useMemo(
    () => (isMultiTenant ? buildKitchenTicketQuantitySummary(event, activeRsvps) : []),
    [isMultiTenant, event, activeRsvps]
  );

  const officeEmail =
    event?.venue?.email_address_office || unit?.contactEmail || 'Not Specified';
  const diningEmail =
    event?.venue?.email_address_dining ||
    event?.venue?.email_address_accounts ||
    'Not Specified';
  const ceremonyStartTime = event?.date
    ? event.date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
  const meetingDateFormatted = event?.date
    ? event.date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : 'Not set';
  const organiserName =
    (eventExtras?.diningOrganiser as string) ||
    (eventExtras?.organizer as string) ||
    (userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`
      : userProfile?.name || '');
  const contactEmail =
    (eventExtras?.contactEmail as string) ||
    unit?.contactEmail ||
    userProfile?.email ||
    'Not provided';

  const handleGeneratePdf = useCallback(async () => {
    if (!unitId || !eventId) {
      showError('Event details are not available yet.');
      return;
    }

    setGeneratingPdf(true);

    try {
      const snapshot = await fetchKitchenReportSnapshotFromServer(unitId, eventId);
      if (!snapshot) {
        showError('Event details are not available yet.');
        return;
      }

      const { unit: pdfUnit, event: pdfEvent, rsvps: pdfRsvps, tableAssignments: pdfTable } =
        snapshot;

      if (pdfRsvps.length === 0) {
        showError('No RSVP data available to generate a booking form.');
        return;
      }

      setUnit(pdfUnit);
      setEvent(pdfEvent);
      setRsvps(pdfRsvps);
      setTableAssignments(pdfTable);

      const pdfActiveRsvps = getKitchenDiners(pdfEvent, pdfRsvps);
      const pdfNumbersDining = getKitchenNumbersDining(pdfActiveRsvps, pdfRsvps);
      const pdfCourseSummaries = buildKitchenCourseSummaries(pdfEvent, pdfActiveRsvps);
      const pdfTicketCourseSummaries = buildKitchenTicketCourseSummaries(
        pdfEvent,
        pdfActiveRsvps
      );
      const pdfCombinedCourseSummaries = buildKitchenCombinedCourseSummaries(
        pdfTicketCourseSummaries,
        pdfCourseSummaries
      );
      const pdfFallbackMealCounts = buildKitchenFallbackMealCounts(pdfActiveRsvps);
      const pdfDietaryRequirements = buildKitchenDietaryRequirements(
        pdfActiveRsvps,
        pdfTable
      );

      const pdfEventExtras = pdfEvent as unknown as Record<string, any>;
      const pdfMeetingDateFormatted = pdfEvent.date
        ? pdfEvent.date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : 'Not set';
      const pdfCeremonyStartTime = pdfEvent.date
        ? pdfEvent.date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : '';
      const pdfOrganiserName =
        (pdfEventExtras?.diningOrganiser as string) ||
        (pdfEventExtras?.organizer as string) ||
        (userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : userProfile?.name || '');
      const pdfContactEmail =
        (pdfEventExtras?.contactEmail as string) ||
        pdfUnit?.contactEmail ||
        userProfile?.email ||
        'Not provided';

      const formatYesNo = (value: string) => {
        if (value === 'Yes' || value === 'No') {
          return value;
        }
        return 'Not specified';
      };

      const lodgeChapter = pdfUnit?.name
        ? `${pdfUnit.name}${pdfUnit.unitNumber && unitDisplaysNumber(pdfUnit.typeId, unitTypes) ? ` No. ${pdfUnit.unitNumber}` : ''}`
        : pdfEvent.unitId;

      const additionalItemsRaw = formData.additionalItems.trim();
      const additionalItemsLines =
        additionalItemsRaw.length > 0
          ? additionalItemsRaw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
          : [];
      const additionalItemsValue = additionalItemsLines.length > 0 ? additionalItemsLines : ['None'];

      const infoRowsBeforeNumbers = [
        { label: 'Lodge/Chapter', value: lodgeChapter },
        { label: 'Date of meeting', value: pdfMeetingDateFormatted },
        { label: 'Dining organiser', value: pdfOrganiserName || 'Not specified' },
        { label: 'Contact telephone no', value: formData.contactTelephone || 'Not specified' },
        { label: 'Contact email', value: pdfContactEmail },
        { label: 'Ceremony start time', value: pdfCeremonyStartTime || 'Not specified' },
        { label: 'Installation', value: formatYesNo(formData.installation) },
        { label: 'Election', value: formatYesNo(formData.election) },
        {
          label: 'Preferred dining time (not guaranteed)',
          value: formData.preferredDiningTime || 'Not specified',
        },
        {
          label: 'Preferred dining room (not guaranteed)',
          value: formData.preferredDiningRoom || 'Not specified',
        },
      ];

      const mealSelectionLines: string[] = [];
      const useSplitView =
        formData.mealDisplayMode === 'split' && pdfTicketCourseSummaries.length > 0;
      const pdfIsMultiTenant = isMultiTenantEvent(pdfEvent);
      const pdfTicketQuantitySummary = pdfIsMultiTenant
        ? buildKitchenTicketQuantitySummary(pdfEvent, pdfActiveRsvps)
        : [];

      if (pdfIsMultiTenant) {
        if (pdfTicketQuantitySummary.length > 0) {
          pdfTicketQuantitySummary.forEach((ticket) => {
            mealSelectionLines.push(`${ticket.qty} - ${ticket.ticketName}`);
          });
        } else {
          mealSelectionLines.push('No tickets recorded.');
        }
      } else if (useSplitView) {
        pdfTicketCourseSummaries.forEach((ticket) => {
          mealSelectionLines.push(ticket.ticketName);
          ticket.courses.forEach((course) => {
            mealSelectionLines.push(`  ${course.courseName}`);
            const populated = course.options.filter((option) => option.count > 0);
            if (populated.length > 0) {
              populated.forEach((option) => {
                mealSelectionLines.push(`    ${option.count} - ${option.optionName}`);
              });
            } else {
              mealSelectionLines.push('    No selections');
            }
          });
        });
      } else if (pdfCombinedCourseSummaries.length > 0) {
        pdfCombinedCourseSummaries.forEach((course) => {
          mealSelectionLines.push(course.name);
          const populated = course.options.filter((option) => option.count > 0);
          if (populated.length > 0) {
            populated.forEach((option) => {
              mealSelectionLines.push(`  ${option.count} - ${option.name}`);
            });
          } else {
            mealSelectionLines.push('  No selections');
          }
        });
      } else if (pdfFallbackMealCounts.length > 0) {
        pdfFallbackMealCounts.forEach((item) => {
          mealSelectionLines.push(`${item.count} - ${item.name}`);
        });
      } else {
        mealSelectionLines.push('No meal selections recorded.');
      }

      const aLaCarteLinesRaw = formData.aLaCarteMenu
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      const aLaCarteLines = aLaCarteLinesRaw.length > 0 ? aLaCarteLinesRaw : ['Not specified'];

      const ticketGroups = useSplitView
        ? pdfTicketCourseSummaries
            .map((ticket) => {
              const courses = ticket.courses
                .map((course) => {
                  const options = course.options
                    .filter((option) => option.count > 0)
                    .map((option) => ({
                      option: option.optionName,
                      count: option.count,
                    }));
                  return {
                    name: course.courseName,
                    options,
                  };
                })
                .filter((course) => course.options.length > 0);

              return {
                title: ticket.ticketName,
                courses,
              };
            })
            .filter((ticket) => ticket.courses.length > 0)
        : [];

      const courseGroups = useSplitView
        ? []
        : pdfCombinedCourseSummaries
            .map((course) => {
              const options = course.options
                .filter((option) => option.count > 0)
                .map((option) => ({
                  option: option.name,
                  count: option.count,
                }));
              return {
                name: course.name,
                options,
              };
            })
            .filter((course) => course.options.length > 0);

      const fallbackMeals = pdfFallbackMealCounts
        .filter((item) => item.count > 0)
        .map((item) => ({
          name: item.name,
          count: item.count,
        }));

      const dietaryRequirementLines = pdfDietaryRequirements.map((entry) => {
        const seatLocation = entry.tableName
          ? `${entry.tableName}${entry.seatNumber ? ` · Seat ${entry.seatNumber}` : ''}`
          : 'Table unassigned';
        return `${entry.name}: ${entry.requirement} (${seatLocation})`;
      });
      const dietaryValue = dietaryRequirementLines.length > 0 ? dietaryRequirementLines : ['No dietary requirements recorded.'];

      const courseColumnSet = new Set<string>();

      const rsvpDetails = pdfActiveRsvps.map((rsvp) => {
        const tableInfo = pdfTable[rsvp.id];
        const roomName = tableInfo?.roomName || 'Unassigned';
        const tableName = tableInfo?.tableName || 'Table';
        const seatLabel = tableInfo?.seatNumber ? String(tableInfo.seatNumber) : '—';

        const ticketSummaryLines: string[] = [];
        const courseSelections: Record<string, string[]> = {};

        const normalizedTc = normalizeTicketCoursesToFormatA(rsvp.responses?.TicketCourses);
        if (pdfEvent.tickets && pdfEvent.tickets.length > 0 && Array.isArray(rsvp.responses?.Tickets)) {
          (rsvp.responses.Tickets as Array<{ id?: string; name?: string; qty?: number }>).
            filter((ticketRow) => ticketRow && ticketRow.id).
            forEach((ticketResponse) => {
              const ticketType = pdfEvent.tickets?.find((t) => t.id === ticketResponse.id);
              if (!ticketType) {
                return;
              }

              const ticketName = ticketResponse.name || ticketType.name || 'Ticket';
              const qtySuffix = ticketResponse.qty && ticketResponse.qty > 1 ? ` x${ticketResponse.qty}` : '';
              ticketSummaryLines.push(`${ticketName}${qtySuffix}`);

              const ticketData = normalizedTc[ticketType.id as string];
              const selectionMaps: Array<Record<string, string>> = [];
              if (ticketData && typeof ticketData === 'object') {
                Object.values(ticketData).forEach((instanceSelections) => {
                  if (instanceSelections && typeof instanceSelections === 'object') {
                    selectionMaps.push(instanceSelections);
                  }
                });
              }

              ticketType.courses?.forEach((course) => {
                courseColumnSet.add(course.name);
                if (!courseSelections[course.name]) {
                  courseSelections[course.name] = [];
                }
                const selections: string[] = [];
                selectionMaps.forEach((instanceMap) => {
                  const optionValue = instanceMap[course.id];
                  if (optionValue !== undefined && optionValue !== null && optionValue !== '') {
                    selections.push(String(optionValue));
                  }
                });
                if (selections.length > 0) {
                  courseSelections[course.name].push(selections.join(', '));
                }
              });
            });
        }

        if (ticketSummaryLines.length === 0) {
          ticketSummaryLines.push('—');
        }

        const allergyText = typeof rsvp.responses?.Allergies === 'string' && rsvp.responses.Allergies.trim().length > 0
          ? rsvp.responses.Allergies.trim()
          : '—';

        return {
          guestName: rsvp.name || `${rsvp.firstName ?? ''} ${rsvp.lastName ?? ''}`.trim() || 'Guest',
          roomName,
          tableName,
          seatNumber: seatLabel,
          ticketSummary: ticketSummaryLines,
          courseSelections,
          allergies: allergyText,
        };
      });

      const courseColumns = Array.from(courseColumnSet);
      if (courseColumns.length === 0) {
        courseColumns.push('Courses');
      }

      rsvpDetails.forEach((detail) => {
        courseColumns.forEach((column) => {
          if (!detail.courseSelections[column] || detail.courseSelections[column].length === 0) {
            detail.courseSelections[column] = ['No selection'];
          }
        });
      });

      const rsvpDetailsSortedBySeat = sortBookingFormGuestsBySeatAsc(rsvpDetails);

      const infoRowsAfterNumbers = [
        {
          label: 'Meal selection - indicate numbers for each choice',
          value: mealSelectionLines,
        },
        {
          label: 'A la carte menu selection',
          value: aLaCarteLines,
        },
        {
          label: 'Dietary requirements',
          value: dietaryValue,
        },
        {
          label: 'Room layout (sprigs or round tables)',
          value: formData.roomLayout || 'Not specified',
        },
        { label: 'Candles/Candelabras', value: formatYesNo(formData.candlesCandelabras) },
        { label: 'Linen tablecloths', value: formatYesNo(formData.linenTablecloths) },
        { label: 'Linen napkins', value: formatYesNo(formData.linenNapkins) },
        { label: 'Gun Room', value: formatYesNo(formData.gunRoom) },
        { label: 'Bar tab required', value: formatYesNo(formData.barTabRequired) },
        { label: 'Bar tab contact', value: formData.barTabContact || 'Not specified' },
        { label: 'Additional items', value: additionalItemsValue },
      ];

      await generateBookingFormPdf({
        eventName: pdfEvent.name,
        eventId: pdfEvent.id,
        generatedDate: new Date().toLocaleDateString('en-GB'),
        unitDisplayName: lodgeChapter,
        infoRowsBeforeNumbers,
        infoRowsAfterNumbers,
        numbersDining: pdfNumbersDining,
        mealSelection: {
          ticketGroups,
          courseGroups,
          fallback: fallbackMeals,
        },
        dietaryRequirements: dietaryRequirementLines,
        rsvpDetails: rsvpDetailsSortedBySeat,
        courseColumns,
      });
    } catch (error) {
      console.error('Error generating booking form PDF:', error);
      showError('Unable to generate the booking form PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  }, [unitId, eventId, formData, userProfile, unitTypes]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
            <p className="text-muted-foreground">Loading booking form data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Error Loading Data</CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  // Retry by re-fetching
                  const fetchData = async () => {
                    if (!unitId || !eventId) {
                      navigate('/dashboard');
                      return;
                    }
                    try {
                      setError(null);
                      const snapshot = await fetchKitchenReportSnapshotFromServer(unitId, eventId);
                      if (!snapshot) {
                        navigate('/dashboard');
                        return;
                      }
                      setUnit(snapshot.unit);
                      setEvent(snapshot.event);
                      setRsvps(snapshot.rsvps);
                      setTableAssignments(snapshot.tableAssignments);
                      setLoading(false);
                    } catch (err) {
                      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
                      setError(errorMessage);
                      setLoading(false);
                    }
                  };
                  fetchData();
                }}
                variant="default"
              >
                Retry
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
              >
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!event || !rsvps.length) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>No Data Available</CardTitle>
              <CardDescription>Unable to load event or RSVP data.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="default"
              >
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-muted text-foreground">
      <Header />

      <main className="flex-1 w-full min-w-0 py-6 sm:py-8 print:py-0">
        <DashboardContentContainer className="print:max-w-none print:px-8">
          <div className="mb-6 flex flex-col gap-2 rounded-lg border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 print:hidden">
            <Button variant="outline" onClick={() => navigate(`/event/${eventId}`)}>
              Back to Event
            </Button>
            <EventHeaderCredits unitId={unitId} />
            <Button
              variant="default"
              className="w-full sm:ml-auto sm:w-auto"
              onClick={handleGeneratePdf}
              disabled={generatingPdf}
            >
              {generatingPdf ? 'Generating…' : 'Generate PDF'}
            </Button>
          </div>

          <div
            className={`bg-white px-4 py-6 text-slate-800 shadow-xl ring-1 ring-slate-200 sm:px-6 md:px-12 md:py-12 print:shadow-none print:ring-0 dark:ring-slate-700 ${KITCHEN_FORM_PAPER_CONTROL_CLASSES}`}
          >
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              {unit?.settings?.logoUrl ? (
                <img
                  src={unit.settings.logoUrl}
                  alt={`${unit.name} logo`}
                  className="h-16 w-auto object-contain"
                />
              ) : (
                <div className="text-lg font-semibold text-[#1f3a75]">{unit?.name}</div>
              )}
              <div className="text-right text-xs text-slate-500">
                <p>Generated on {new Date().toLocaleDateString('en-GB')}</p>
                <p>Event ID: {event.id}</p>
              </div>
            </div>

            <div className="mt-4 text-center">
              <h1 className="text-2xl font-bold uppercase tracking-wide text-[#1f3a75]">
                Final Booking Form
              </h1>
              <p className="mt-2 text-sm font-semibold uppercase text-red-600">
                To be submitted at least 3 working days prior to meeting
              </p>
              <p className="mt-4 text-sm text-slate-600">
                Please complete this form and return via email to:
                <span className="ml-1 font-semibold text-[#1f3a75]">{officeEmail}</span>
              </p>
              <p className="text-sm text-slate-600">
                Please make sure to CC your email to:
                <span className="ml-1 font-semibold text-[#1f3a75]">{diningEmail}</span>
              </p>
            </div>

            <table className="mt-6 w-full table-fixed border border-slate-300 text-sm text-slate-800">
              <thead className="bg-[#1f3a75] text-white">
                <tr>
                  <th className="border border-slate-300 px-4 py-2 text-left font-semibold uppercase tracking-wide">
                    Day of dining
                  </th>
                  <th className="border border-slate-300 px-4 py-2 text-left font-semibold uppercase tracking-wide">
                    Form to be returned by 5:00pm on the preceding
                  </th>
                </tr>
              </thead>
              <tbody>
                {SUBMISSION_SCHEDULE.map((row) => (
                  <tr key={row.day} className="odd:bg-slate-100">
                    <td className="border border-slate-300 px-4 py-2">{row.day}</td>
                    <td className="border border-slate-300 px-4 py-2">{row.returnBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-8 border border-slate-300">
              <LabelValueRow label="Lodge/Chapter">
                {unit?.name ? `${unit.name}${unitDisplaysNumber(unit.typeId, unitTypes) ? ` No. ${unit.unitNumber || 'N/A'}` : ''}` : event.unitId}
              </LabelValueRow>
              <LabelValueRow label="Date of meeting">{meetingDateFormatted}</LabelValueRow>
              <LabelValueRow label="Dining organiser">{organiserName || 'Not specified'}</LabelValueRow>
              <LabelValueRow label="Contact telephone no">
                <Input
                  value={formData.contactTelephone}
                  onChange={handleTextFieldChange('contactTelephone')}
                  onBlur={handleTextFieldBlur('contactTelephone', 'contactPhone')}
                  placeholder="Enter contact telephone number"
                />
              </LabelValueRow>
              <LabelValueRow label="Contact email">{contactEmail}</LabelValueRow>
              <LabelValueRow label="Ceremony start time">
                {ceremonyStartTime || 'Not specified'}
              </LabelValueRow>
              <LabelValueRow label="Installation">
                <Select
                  value={formData.installation || undefined}
                  onValueChange={handleSelectChange('installation', 'installation', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Election">
                <Select
                  value={formData.election || undefined}
                  onValueChange={handleSelectChange('election', 'election', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Preferred dining time (not guaranteed)">
                <Input
                  value={formData.preferredDiningTime}
                  onChange={handleTextFieldChange('preferredDiningTime')}
                  onBlur={handleTextFieldBlur('preferredDiningTime', 'diningTime')}
                  placeholder="Enter preferred dining time"
                />
              </LabelValueRow>
              <LabelValueRow label="Preferred dining room (not guaranteed)">
                <Input
                  value={formData.preferredDiningRoom}
                  onChange={handleTextFieldChange('preferredDiningRoom')}
                  onBlur={handleTextFieldBlur('preferredDiningRoom', 'diningRoom')}
                  placeholder="Enter preferred dining room"
                />
              </LabelValueRow>
              <LabelValueRow label="Numbers dining">
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-[#1f3a75]">{numbersDining}</p>
                </div>
              </LabelValueRow>
              <div className="grid grid-cols-12 border-b border-slate-300">
                <div className="col-span-4 flex flex-col gap-3 border-r border-slate-300 bg-[#1f3a75] px-4 py-3 text-sm font-semibold text-white">
                  <span>{isMultiTenant ? 'Ticket quantities' : 'Meal selection - indicate numbers for each choice'}</span>
                  {!isMultiTenant && (
                    <Select
                      value={formData.mealDisplayMode}
                      onValueChange={handleSelectChange('mealDisplayMode', 'mealDisplayMode')}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Please Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {MEAL_DISPLAY_MODE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="col-span-8 px-4 py-3 text-sm text-slate-800">
                  {isMultiTenant ? (
                    ticketQuantitySummary.length > 0 ? (
                      <div className="space-y-1">
                        {ticketQuantitySummary.map((ticket) => (
                          <div
                            key={ticket.ticketId}
                            className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2"
                          >
                            <span className="text-sm">{ticket.ticketName}</span>
                            <span className="text-base font-semibold text-[#1f3a75]">{ticket.qty}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No tickets recorded.</p>
                    )
                  ) : formData.mealDisplayMode === 'split' && ticketCourseSummaries.length > 0 ? (
                    <div className="space-y-6">
                      {ticketCourseSummaries.map((ticket) => (
                        <div key={ticket.ticketId} className="space-y-3">
                          <p className="text-base font-semibold uppercase text-[#1f3a75] underline decoration-2">
                            {ticket.ticketName}
                          </p>
                          <div className="space-y-3">
                            {ticket.courses.map((course) => (
                              <div key={course.courseId} className="space-y-2">
                                <p className="text-sm font-semibold text-[#1f3a75]">
                                  {course.courseName}
                                </p>
                                <div className="space-y-1">
                                  {course.options.map((option) => (
                                    <div
                                      key={option.optionName}
                                      className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2"
                                    >
                                      <span className="text-sm">{option.optionName}</span>
                                      <span className="text-base font-semibold text-[#1f3a75]">
                                        {option.count}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : combinedCourseSummaries.length > 0 ? (
                    <div className="grid gap-6 md:grid-cols-2">
                      {combinedCourseSummaries.map((course) => (
                        <div key={course.name}>
                          <p className="text-sm font-semibold uppercase text-[#1f3a75]">
                            {course.name}
                          </p>
                          <div className="mt-2 space-y-2">
                            {course.options.map((option) => (
                              <div
                                key={option.name}
                                className="flex items-center justify-between rounded border border-slate-200 bg-slate-50 px-3 py-2"
                              >
                                <span className="text-sm">{option.name}</span>
                                <span className="text-base font-semibold text-[#1f3a75]">
                                  {option.count}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No meal selections recorded.</p>
                  )}
                </div>
              </div>
              <LabelValueRow label="Dietary requirements">
                {dietaryRequirements.length > 0 ? (
                  <ul className="space-y-1">
                    {dietaryRequirements.map((entry) => {
                      const assignmentLabel = entry.tableName
                        ? `${entry.tableName}${entry.seatNumber ? ` · Seat ${entry.seatNumber}` : ''}`
                        : 'Table unassigned';

                      return (
                        <li key={entry.id} className="flex items-start">
                          <span className="mr-2 text-[#1f3a75]">•</span>
                          <span>
                            {entry.name}: {entry.requirement}
                            <span className="ml-2 text-xs text-slate-500">({assignmentLabel})</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p>No dietary requirements recorded.</p>
                )}
              </LabelValueRow>
              <LabelValueRow label="Room layout (sprigs or round tables)">
                <Select
                  value={formData.roomLayout || undefined}
                  onValueChange={handleSelectChange('roomLayout', 'roomLayout')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_LAYOUT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Candles/Candelabras">
                <Select
                  value={formData.candlesCandelabras || undefined}
                  onValueChange={handleSelectChange('candlesCandelabras', 'candlesRequired', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Linen tablecloths">
                <Select
                  value={formData.linenTablecloths || undefined}
                  onValueChange={handleSelectChange('linenTablecloths', 'linenTablecloths', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Linen napkins">
                <Select
                  value={formData.linenNapkins || undefined}
                  onValueChange={handleSelectChange('linenNapkins', 'linenNapkins', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Gun Room">
                <Select
                  value={formData.gunRoom || undefined}
                  onValueChange={handleSelectChange('gunRoom', 'gunRoom', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Bar tab required">
                <Select
                  value={formData.barTabRequired || undefined}
                  onValueChange={handleSelectChange('barTabRequired', 'barTabRequired', true)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Please Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {YES_NO_OPTIONS.map((option) => (
                      <SelectItem key={option.label} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </LabelValueRow>
              <LabelValueRow label="Bar tab contact">
                <Input
                  value={formData.barTabContact}
                  onChange={handleTextFieldChange('barTabContact')}
                  onBlur={handleTextFieldBlur('barTabContact', 'barTabContact')}
                  placeholder="Enter bar tab contact"
                />
              </LabelValueRow>
              <LabelValueRow label="Additional items">
                <Textarea
                  value={formData.additionalItems}
                  onChange={handleTextFieldChange('additionalItems')}
                  onBlur={handleTextFieldBlur('additionalItems', 'additionalRequirements')}
                  placeholder="Add any additional requirements or notes"
                  rows={4}
                />
              </LabelValueRow>
            </div>
          </div>
        </DashboardContentContainer>
      </main>
    </div>
  );
}
